import { useId } from 'react';
import { BlockStack } from '../block-stack/block-stack';
import { Typography } from '../typography/typography';
import { Icon, type IconName } from '../icon/icon';
import { fieldShell, fieldIcon, fieldInput, fieldError } from './text-field.css';

export interface TextFieldProps {
    value: string;
    onChange: (value: string) => void;
    /** Visible label, associated with the input for assistive tech. */
    label?: string;
    /**
     * Accessible name when there is no visible `label` (e.g. an icon-only search
     * field). Required if `label` is omitted — the input must never be unlabelled.
     */
    accessibilityLabel?: string;
    placeholder?: string;
    /** Leading glyph inside the field (e.g. `search`). Decorative — hidden from AT. */
    leadingIcon?: IconName;
    type?: 'text' | 'search' | 'email' | 'number';
    inputMode?: 'text' | 'numeric' | 'decimal';
    min?: number;
    max?: number;
    disabled?: boolean;
    /** Error message; shown below and wired via `aria-describedby` + `aria-invalid`. */
    error?: string;
    /** Fired on Enter — handy for single-field "set" affordances. */
    onSubmitEditing?: () => void;
    testID?: string;
}

/**
 * Single-line text input — Untitled UI style: rounded shell, hairline border,
 * primary focus ring, optional leading icon. Never pins a fixed height (min-height
 * + padding), and 16px text so it scales with the OS type setting without zoom.
 *
 * Doubles as a search field (icon-only, `accessibilityLabel`) and a labelled form
 * field with optional numeric constraints, submit-on-Enter, and inline errors.
 */
export function TextField({
    value,
    onChange,
    label,
    accessibilityLabel,
    placeholder,
    leadingIcon,
    type = 'text',
    inputMode,
    min,
    max,
    disabled,
    error,
    onSubmitEditing,
    testID,
}: TextFieldProps) {
    const inputId = useId();
    const errorId = `${inputId}-error`;

    const input = (
        <div className={fieldShell} data-invalid={error ? true : undefined}>
            {leadingIcon ? (
                <span className={fieldIcon}>
                    <Icon name={leadingIcon} />
                </span>
            ) : null}
            <input
                id={label ? inputId : undefined}
                className={fieldInput}
                type={type}
                inputMode={inputMode}
                value={value}
                placeholder={placeholder}
                min={min}
                max={max}
                disabled={disabled}
                aria-label={label ? undefined : accessibilityLabel}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                data-testid={testID}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' && onSubmitEditing) {
                        event.preventDefault();
                        onSubmitEditing();
                    }
                }}
            />
        </div>
    );

    // Bare input only when there's nothing to wrap it with.
    if (!label && !error) return input;

    return (
        <BlockStack gap="150">
            {label ? (
                <label htmlFor={inputId}>
                    <Typography as="span" variant="body-sm" fontWeight="medium" tone="text-secondary">
                        {label}
                    </Typography>
                </label>
            ) : null}
            {input}
            {error ? (
                <span id={errorId} className={fieldError} role="alert">
                    {error}
                </span>
            ) : null}
        </BlockStack>
    );
}
